import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { DataWarehouseCopySink } from 'app/model/pipeline/copysink/data-warehouse.copysink';
import { Activity, AdapterContext, arrayIsNullOrEmpty, IModel, Owned, ParameterConfig, SharedModel } from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { ValueAdapter } from '../../value.adapter';
import { copyMethodOptionValues } from 'app/shared/components/copy-method-setting/copy-method-setting.const';
import { TabularSinkAdapter } from './tabular.sink.adapter';
import { CopySinkAdapter } from './copysink.adapter';
import { toParameterClass, toParameterObject } from '../../linkedservices/parameter-config-string.util';
import { CopyCommandSetting, IColumnDefaultValue } from 'app/model/dataset/copy-command-setting';

export const adapter = {
    type: '*',
    writeBatchTimeout: '*',
    writeBatchSize: '*',
    preCopyScript: '*',
    maxConcurrentConnections: '*',
    structure: '*',
    tableOption: '*',
    disableMetricsCollection: '*',
    allowCopyCommand: '*',
    copyCommandSettings: {
        defaultValues: '*',
        additionalOptions: '*'
    },
    writeBehavior: '*',
    upsertSettings: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DataWarehouseCopySink),
    asynchronous: true,
    adapts: adapter
})
export class DataWarehouseSinkAdapter extends CopySinkAdapter<DataWarehouseCopySink, Model.SqlDWSink> {
    protected model(): IModel {
        return DataWarehouseCopySink.MODEL;
    }

    protected override async _toResource(model: DataWarehouseCopySink): Promise<Model.SqlDWSink> {
        const result: Model.SqlDWSink = {
            type: this.model().name
        };
        return await DataWarehouseSinkAdapter.toResourceInternal(model, result, this.context);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected static _upsertToResource(result: Model.SqlSink, model: DataWarehouseCopySink): void {
        // for copy-wizard generated activities, where upsertSettings parameterized as one
        const upsertSettings = ValueAdapter.toResource(model, DataWarehouseCopySink.UPSERT_SETTINGS);
        if (upsertSettings?.type === 'Expression') {
            result.upsertSettings = upsertSettings;
        } else {
            result.upsertSettings = {
                keys: ValueAdapter.toResource(model, DataWarehouseCopySink.WRITE_BEHAVIOR_KEYS)
            };
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static async toResourceInternal(
        model: DataWarehouseCopySink,
        result: Model.SqlDWSink,
        context: AdapterContext
    ): Promise<Model.SqlDWSink> {
        TabularSinkAdapter.toSinkResource(model, result);

        result.allowCopyCommand = ValueAdapter.toResource(model, DataWarehouseCopySink.ALLOW_COPY_COMMAND);
        if (result.allowCopyCommand && model.copyCommandSettings != null) {
            result.copyCommandSettings = {};
            result.copyCommandSettings.additionalOptions = Array.isArray(
                model.copyCommandSettings.additionalOptions
            )
                ? toParameterObject(model.copyCommandSettings.additionalOptions)
                : ValueAdapter.toResource(model.copyCommandSettings, CopyCommandSetting.ADDITIONAL_OPTIONS);
            result.copyCommandSettings.defaultValues = Array.isArray(model.copyCommandSettings.defaultValues)
                ? !arrayIsNullOrEmpty(model.copyCommandSettings.defaultValues)
                    ? model.copyCommandSettings.defaultValues
                            .filter((result) => result)
                            .map((value: ParameterConfig) => ({
                                columnName: value.name,
                                defaultValue: value.value
                            }))
                    : undefined
                : ValueAdapter.toResource(model.copyCommandSettings, CopyCommandSetting.DEFAULT_VALUES);
        }

        if (__IS_FABRIC_DPE__) {
            result.writeBehavior = ValueAdapter.toResource(model, DataWarehouseCopySink.COPY_METHOD);
            const isExpressionWriteBehavior = DataWarehouseCopySink.COPY_METHOD.templateValue(model) != null;
            if (isExpressionWriteBehavior || model.copyMethod === copyMethodOptionValues.Upsert) {
                DataWarehouseSinkAdapter._upsertToResource(result, model);
            } else if (model.copyMethod === copyMethodOptionValues.SCD2) {
                DataWarehouseSinkAdapter._upsertToResource(result, model);
            }
        }

        result.tableOption = ValueAdapter.toResource(model, DataWarehouseCopySink.TABLE_OPTION);
        result.disableMetricsCollection = ValueAdapter.toResource(model, DataWarehouseCopySink.DISABLE_METRICS_COLLECTION);
        CopySinkAdapter.toCommonCopySinkResource(model, result, context);
        return result;
    }

    protected override async _toModel(resource: Model.SqlDWSink, owner: Activity): Promise<DataWarehouseCopySink> {
        const result = Owned.create<DataWarehouseCopySink>(this.model(), owner);
        return await DataWarehouseSinkAdapter.toModelInternal(resource, result, this.context);
    }
    
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static async toModelInternal(
        resource: Model.SqlDWSink,
        result: DataWarehouseCopySink,
        context: AdapterContext
    ): Promise<DataWarehouseCopySink> {
        TabularSinkAdapter.toSinkModel(resource, result);
        if (resource.allowCopyCommand) {
            ValueAdapter.toModel(result, DataWarehouseCopySink.ALLOW_COPY_COMMAND, resource.allowCopyCommand);
            if (resource.copyCommandSettings != null) {
                result.copyCommandSettings = Owned.create<CopyCommandSetting>(CopyCommandSetting.MODEL, result);
                if (
                    resource.copyCommandSettings.additionalOptions &&
                    typeof resource.copyCommandSettings.additionalOptions === 'object' &&
                    resource.copyCommandSettings.additionalOptions.type !== 'Expression'
                ) {
                    result.copyCommandSettings.additionalOptions = toParameterClass(
                        resource.copyCommandSettings.additionalOptions,
                        result.copyCommandSettings
                    );
                } else {
                    ValueAdapter.toModel(
                        result.copyCommandSettings,
                        CopyCommandSetting.ADDITIONAL_OPTIONS,
                        resource.copyCommandSettings.additionalOptions
                    );
                }
                if (
                    resource.copyCommandSettings.defaultValues &&
                    Array.isArray(resource.copyCommandSettings.defaultValues)
                ) {
                    result.copyCommandSettings.defaultValues = resource.copyCommandSettings.defaultValues.map(
                        (setting: IColumnDefaultValue) => {
                            const config = new ParameterConfig();
                            config.name = setting.columnName;
                            config.value = setting.defaultValue;
                            return config;
                        }
                    );
                } else {
                    ValueAdapter.toModel(
                        result.copyCommandSettings,
                        CopyCommandSetting.DEFAULT_VALUES,
                        resource.copyCommandSettings.defaultValues
                    );
                }
            }
        }
        if (resource.writeBehavior !== undefined && __IS_FABRIC_DPE__) {
            ValueAdapter.toModel(result, DataWarehouseCopySink.COPY_METHOD, resource.writeBehavior);

            // for copy-wizard generated activities, where upsertSettings parameterized as one
            if (resource.writeBehavior !== copyMethodOptionValues.BulkInsert && resource.upsertSettings) {
                if ((resource.upsertSettings as SharedModel.ExpressionValue)?.type === 'Expression') {
                    ValueAdapter.toModel(result, DataWarehouseCopySink.UPSERT_SETTINGS, resource.upsertSettings);
                } else {
                    const upsertSettings = resource.upsertSettings as Model.WriteBehaviorUpsertOptions;
                    ValueAdapter.toModel(result, DataWarehouseCopySink.WRITE_BEHAVIOR_KEYS, upsertSettings.keys);
                }
            }
        } else {
            result.copyMethod = undefined;
        }

        ValueAdapter.toModel(result, DataWarehouseCopySink.TABLE_OPTION, resource.tableOption);
        ValueAdapter.toModel(result, DataWarehouseCopySink.DISABLE_METRICS_COLLECTION, resource.disableMetricsCollection);
        CopySinkAdapter.toCommonCopySinkModel(result, resource);

        return result;
    }
}
